import { Component, inject, OnInit } from '@angular/core';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { Router } from '@angular/router';
import { DynamicRouteService } from 'core';
import { LoadingSpinner, TableColumnInfo, TableView, TypeOfColumn } from 'design-system';
import { finalize } from 'rxjs';
import {
  Receipt,
  ReceiptListSearch,
  ReceiptStatus,
  RECEIPT_STATUS_IN_PROGRESS,
} from '../../interfaces/receipt.model';
import { ReceiptsService } from '../../services/receipts.service';
import { formatDateLabel, toRouteSegments } from '../../utils/receipt.utils';
import { RECEIPTS_DESCRIBE } from '../constants';

interface ReceiptRow {
  id: number;
  idBusinessPartner: string;
  zona: string;
  cliente: string;
  dataIncasso: string;
  situazione: string;
}

const STATUS_LABELS: Record<string, string> = {
  [ReceiptStatus.Nuovo]: 'Nuovo',
  [ReceiptStatus.InLavorazione]: 'In lavorazione',
  [ReceiptStatus.Chiuso]: 'Chiuso',
  [ReceiptStatus.Archiviato]: 'Archiviato',
  [ReceiptStatus.Versato]: 'Versato',
};

@Component({
  selector: 'rev-receipts-in-progress',
  imports: [LoadingSpinner, MatPaginatorModule, TableView],
  templateUrl: './receipts-in-progress.html',
  styleUrl: './receipts-in-progress.scss',
})
export class ReceiptsInProgress implements OnInit {
  private readonly service = inject(ReceiptsService);
  private readonly router = inject(Router);
  private readonly dynamicRouteService = inject(DynamicRouteService);

  incassi: ReceiptRow[] = [];
  isLoading = false;
  totalItems = 0;
  pageSize = 10;
  pageIndex = 0;

  readonly columns: TableColumnInfo[] = [
    { columnNameDef: 'id', label: 'ID', type: TypeOfColumn.TEXT },
    { columnNameDef: 'zona', label: 'Zona', type: TypeOfColumn.TEXT },
    { columnNameDef: 'cliente', label: 'Cliente', type: TypeOfColumn.TEXT },
    { columnNameDef: 'dataIncasso', label: 'Data incasso', type: TypeOfColumn.TEXT },
    { columnNameDef: 'situazione', label: 'Stato', type: TypeOfColumn.TEXT },
  ];

  ngOnInit(): void {
    this.loadReceipts();
  }

  loadReceipts(): void {
    this.isLoading = true;

    const filter: ReceiptListSearch = {
      searchText: null,
      orderBy: null,
      searchObject: { statiIncasso: RECEIPT_STATUS_IN_PROGRESS },
    };

    this.service
      .getReceiptsList(this.pageIndex + 1, this.pageSize, filter)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: data => {
          this.incassi = (data.items ?? []).map(receipt => this.toRow(receipt));
          this.totalItems = data.totalItems ?? 0;
        },
        error: err => {
          console.error('Errore caricamento incassi in lavorazione', err);
          this.incassi = [];
          this.totalItems = 0;
        },
      });
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadReceipts();
  }

  apriIncasso(row: ReceiptRow): void {
    const navigationArray = this.dynamicRouteService.getRoutePath(
      this.router,
      RECEIPTS_DESCRIBE,
      'new-customer-summary',
    );

    if (!navigationArray) {
      console.error('Rotta new-customer-summary non configurata');
      return;
    }

    const target = this.dynamicRouteService.replacePathSegments(
      toRouteSegments(navigationArray),
      [row.idBusinessPartner],
    );

    this.router.navigate(target, {});
  }

  private toRow(receipt: Receipt): ReceiptRow {
    return {
      id: receipt.numeroIncasso,
      idBusinessPartner: receipt.idBusinessPartner,
      zona: receipt.zona,
      cliente: receipt.ragioneSocialeCliente,
      dataIncasso: formatDateLabel(receipt.dataIncasso),
      situazione: STATUS_LABELS[receipt.statoIncasso] ?? receipt.statoIncasso,
    };
  }
}
